import type { ParsedBackendError } from './backend-error.model';

/**
 * Catálogo de códigos del contrato de errores (ola 0). TypeScript puro: CERO
 * Angular.
 *
 * Es la ÚNICA fuente de verdad de qué códigos tienen copy propio. El spec
 * `shared/errors/error-codes-i18n.spec.ts` verifica contra esta constante que
 * `es`/`en`/`pt` no se desincronicen (R17).
 */

/**
 * Códigos con mensaje propio en `errors.codes.*` (peldaño 1 de la escalera).
 *
 * Ausentes a propósito (`design.md` §6.1): `NOT_FOUND`, `FORBIDDEN`,
 * `UNAUTHENTICATED` y `METHOD_NOT_ALLOWED`. No aportan nada sobre el peldaño 2 y,
 * en el caso de `NOT_FOUND`, mapearlo aquí dejaría muerto el parche por URL del
 * 404 de `/commissions/` que D2 ordena conservar hasta la feature 10.
 */
export const MESSAGED_ERROR_CODES = [
  'UPSTREAM_TIMEOUT',
  'UPSTREAM_UNREACHABLE',
  'UPSTREAM_AUTH_FAILED',
  'UPSTREAM_QUERY_FAILED',
  'UPSTREAM_INTERNAL_ERROR',
  'UPSTREAM_REJECTED',
  'UPSTREAM_FAILED',
  // Los dos de la cuarta publicación (§11 bis, «pasada de precisión»). Sin
  // mensaje propio caían al peldaño 2 y se leían «Error interno del servidor
  // (500)» aunque `UPSTREAM_NOT_CONFIGURED` llegue 503 desde links: ése fue el
  // reporte que disparó esa versión del contrato.
  'UPSTREAM_NOT_CONFIGURED',
  'UPSTREAM_TLS_FAILED',
  // Banca #290 (feature 93): el acuse del proveedor no fue interpretable y no se
  // sabe si la operación se aplicó. El texto es GENÉRICO a propósito: por la
  // escalera lo puede recibir cualquier módulo, y "no la repitas" solo es cierto
  // para una orden SPEI (eso lo dicen las pantallas de orden con su propio copy).
  'UPSTREAM_BAD_RESPONSE',
  // Quinta publicación (2026-09-09, feature 17). El ÚNICO código nuevo, y llega
  // sólo desde TPV: significa que se cayó **Memphis DETRÁS del micro**, no que
  // falló el micro (eso es `UPSTREAM_INTERNAL_ERROR`, 502). Se separa porque la
  // espera es otra —un outage del Gateway se resuelve solo; un bug del micro
  // necesita su deploy— y porque manda a mirar a otro equipo.
  //
  // HOY NO SE PUEDE PROVOCAR, y no es un olvido: depende del PR del micro que
  // tipa sus dos `throw` de Memphis como `ServiceUnavailableException` (503).
  // Hasta que entre, este código no llega nunca; cuando entre, llega **sin
  // deploy del front ni de la suite** (el mapeo del backend ya está desplegado y
  // la rama está inerte). Mapearlo ahora es gratis y evita tener que volver.
  'PROVIDER_UNAVAILABLE',
  'DEPENDENCY_UNAVAILABLE',
  'COMPANY_NOT_PROVISIONED',
  'UNIT_NOT_IN_COMPANY',
  'TASA_JERARQUIA',
  'TASA_INVALIDA',
  'INSUFFICIENT_BALANCE',
  'TARGET_NOT_IN_SUBTREE',
  'TARGET_NOT_FOUND',
  'SOURCE_NOT_FOUND',
  'ACCOUNT_NOT_ACTIVE',
  'ACCOUNT_NOT_LINKED',
  'TENANT_REQUIRED',
  'TENANT_AMBIGUOUS',
  'PAYMENTS_MERCHANT_NOT_CONFIGURED',
  'PAYMENTS_TENANT_INACTIVE',
  // Contrato rev. 3 (2026-09-23): HTTP 400 cuando POST /companies/ se llama
  // sin `merchant_id` ni `gateway_account_id`. Se mapea en `charges` y
  // `create-link` con el mismo switch por `error_code`.
  'PAYMENTS_DESTINATION_REQUIRED',
  // Contrato §Parte 2 (cobro directo): HTTP 400 cuando el operador intenta cobrar
  // con tarjeta desde un comercio sin cuenta propia en la pasarela. Sus links
  // siguen funcionando; el arreglo lo hace ops, no el front.
  'PAYMENTS_DIRECT_CHARGE_REQUIRES_ACCOUNT',
  'CHANNEL_NOT_CONFIGURED',
  'LINK_NOT_PENDING',
  'RATE_LIMITED',
  'INSUFFICIENT_FUNDS',
  // Bloqueo del segundo factor por intentos (429). Con `retry_after` la
  // escalera dice los minutos (`errors.totpLocked.minutes`); sin él, este.
  'TOTP_LOCKED',
] as const;

export type MessagedErrorCode = (typeof MESSAGED_ERROR_CODES)[number];

/**
 * Los cinco códigos de sesión del §8 del contrato («Los 401, verificados uno por
 * uno»). `UNAUTHENTICATED` NO está aquí: el contrato pide mandarlo al login, que
 * es un cambio de comportamiento fuera del alcance de esta ola (queda registrado
 * para la feature 10); hoy sigue cayendo al modal genérico, como siempre.
 */
export const SESSION_ERROR_CODES = [
  'SESSION_EXPIRED_INACTIVITY',
  'TOKEN_INVALID',
  'SESSION_INVALIDATED_ELSEWHERE',
  'TOKEN_REVOKED',
  'USER_DISABLED',
] as const;

export type SessionErrorCode = (typeof SESSION_ERROR_CODES)[number];

/**
 * Qué debe hacer el front con un 401.
 *
 * - `refresh`: el token ya no sirve pero la sesión SÍ se puede renovar — caducó
 *   por inactividad, o el access token venció por su propio `exp`.
 * - `invalidate`: el token ya no vale (otro dispositivo, revocado). Modal +
 *   logout, SIN intentar refresh: refrescar un token irrefrescable es un
 *   viaje perdido y un logout tardío.
 * - `disabled`: la cuenta fue deshabilitada. Logout con copy propio.
 * - `none`: no es un 401 de sesión; que siga la cadena normal.
 */
export type SessionAction = 'refresh' | 'invalidate' | 'disabled' | 'none';

/**
 * Paso 2 del orden obligatorio del contrato (§8): decidir por `error_code` CON
 * fallback a las subcadenas en español que el interceptor ya casa hoy.
 *
 * La cuarta rama (código conocido pero no de sesión → `none`) es la que impide
 * un falso positivo: cuando el backend YA habló y dijo otra cosa, casar
 * subcadenas convertiría el código en decoración (R27).
 */
export function classifySessionError(parsed: ParsedBackendError): SessionAction {
  switch (parsed.code) {
    // Los DOS que deben intentar renovar (contrato §8.3, `REINTENTAR_SESION`).
    // `TOKEN_INVALID` es el más común de los dos: lo emite simplejwt cuando el
    // access token vence por su `exp`, sin pasar por el middleware de sesión.
    case 'SESSION_EXPIRED_INACTIVITY':
    case 'TOKEN_INVALID':
      return 'refresh';
    case 'SESSION_INVALIDATED_ELSEWHERE':
    case 'TOKEN_REVOKED':
      return 'invalidate';
    case 'USER_DISABLED':
      return 'disabled';
    default:
      break;
  }

  // R27: el backend mandó un código y no es de sesión. No se re-decide por texto.
  if (parsed.code !== null) return 'none';

  // R26: sin código, exactamente la heurística de hoy
  // (`auth.interceptor.ts` isMultipleSessionError / isTokenExpiredError).
  const detailMsg = parsed.detailText.toLowerCase();
  const errorMsg = parsed.errorText.toLowerCase();
  if (detailMsg.includes('invalidado') || errorMsg.includes('token inválido')) return 'invalidate';
  if (detailMsg.includes('expirado') || errorMsg.includes('expirado')) return 'refresh';
  return 'none';
}
